import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Trophy, Search, ChevronLeft, ChevronRight, ArrowLeft, Filter, X, ChevronRight as Chevron, Download } from "lucide-react";
import { useTableSort, SortableHeader } from "@/lib/tableSort";
import { filterQuotations, computeSellerStats } from "@/lib/sellerRanking";
import * as XLSX from "xlsx";

// First & last day of the current month, as YYYY-MM-DD (local).
const currentMonthRange = () => {
  const n = new Date();
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { from: fmt(new Date(n.getFullYear(), n.getMonth(), 1)), to: fmt(new Date(n.getFullYear(), n.getMonth() + 1, 0)) };
};

const ALL_BRANDS = ["Wellcore", "Dynamite", "YouWeFit", "Okami", "OSMO"];
const PAGE_SIZE = 15;
const fmtMoney = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtNum = (v) => Number(v || 0).toLocaleString("en-IN");
const fmtPct = (v) => `${Number(v || 0).toFixed(2)}%`;

const toggleVal = (list, val) => (list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);

function MultiFilter({ label, open, setOpen, options, selected, setSelected, testId }) {
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs" data-testid={testId}>
          <Filter className="h-3 w-3 mr-1.5" />{label}{selected.length > 0 && <span className="ml-1.5 bg-[#14b8a6] text-white rounded-full px-1.5 text-[10px]">{selected.length}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2">
        <div className="max-h-64 overflow-y-auto space-y-1">
          {options.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">No options</p>}
          {options.map((o) => (
            <div key={o} className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 ${selected.includes(o) ? "bg-[#14b8a6]/10" : ""}`} onClick={() => setSelected(toggleVal(selected, o))}>
              <div className={`w-4 h-4 rounded border flex-shrink-0 ${selected.includes(o) ? "bg-[#14b8a6] border-[#14b8a6]" : "border-slate-300"}`}>
                {selected.includes(o) && <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
              </div>
              <span className="text-sm truncate">{o}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function SellerRevenueRanking() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [quotations, setQuotations] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters inherited from Seller Insights via the URL query string.
  const inherited = useMemo(() => ({
    dateFrom: searchParams.get("dateFrom") || "",
    dateTo: searchParams.get("dateTo") || "",
    selectedBrands: searchParams.get("brands") ? searchParams.get("brands").split(",") : [],
    selectedSkus: searchParams.get("skus") ? searchParams.get("skus").split(",") : [],
    selectedSellers: searchParams.get("sellers") ? searchParams.get("sellers").split(",") : [],
    selectedCities: searchParams.get("cities") ? searchParams.get("cities").split(",") : [],
    selectedStates: searchParams.get("states") ? searchParams.get("states").split(",") : [],
  }), [searchParams]);

  // Page-level (client-side) refinements on top of the inherited ranking.
  const [search, setSearch] = useState("");
  // Date Range filter — defaults to the current month (or inherited range if arriving from Seller Insights).
  const [dateFrom, setDateFrom] = useState(() => inherited.dateFrom || currentMonthRange().from);
  const [dateTo, setDateTo] = useState(() => inherited.dateTo || currentMonthRange().to);
  const [pageStates, setPageStates] = useState([]);
  const [pageCities, setPageCities] = useState([]);
  const [pageBrands, setPageBrands] = useState([]);
  const [minDiscount, setMinDiscount] = useState("");
  const [maxDiscount, setMaxDiscount] = useState("");
  const [minRevenue, setMinRevenue] = useState("");
  const [maxRevenue, setMaxRevenue] = useState("");
  const [page, setPage] = useState(1);
  const [stateOpen, setStateOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [q, s, p] = await Promise.all([
          axios.get(`${API}/quotations`, { withCredentials: true }),
          axios.get(`${API}/sellers`, { withCredentials: true }),
          axios.get(`${API}/products`, { withCredentials: true }),
        ]);
        setQuotations(q.data || []);
        setSellers(s.data || []);
        setProducts(p.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sellerMap = useMemo(() => {
    const m = {};
    sellers.forEach((s) => { m[s.id] = s; });
    return m;
  }, [sellers]);

  // Base ranking with inherited filters + the selected Date Range — used to populate page filter options.
  const inheritedRanking = useMemo(() => {
    const withDate = { ...inherited, dateFrom, dateTo };
    const fq = filterQuotations(quotations, sellerMap, withDate);
    return computeSellerStats(fq, sellerMap, products, withDate);
  }, [quotations, sellerMap, products, inherited, dateFrom, dateTo]);

  // Effective filters = inherited, with the page Date Range + State/City/Brand as overrides.
  const effectiveFilters = useMemo(() => ({
    ...inherited,
    dateFrom,
    dateTo,
    selectedStates: pageStates.length ? pageStates : inherited.selectedStates,
    selectedCities: pageCities.length ? pageCities : inherited.selectedCities,
    selectedBrands: pageBrands.length ? pageBrands : inherited.selectedBrands,
  }), [inherited, dateFrom, dateTo, pageStates, pageCities, pageBrands]);

  // Display ranking = inherited + page filters applied, then ranked.
  const baseRanking = useMemo(() => {
    const fq = filterQuotations(quotations, sellerMap, effectiveFilters);
    return computeSellerStats(fq, sellerMap, products, effectiveFilters).map((s, i) => ({ ...s, rank: i + 1 }));
  }, [quotations, sellerMap, products, effectiveFilters]);

  // Options for the page-level filters (from the inherited base).
  const availStates = useMemo(() => Array.from(new Set(inheritedRanking.map((r) => r.state).filter((v) => v && v !== "-"))).sort(), [inheritedRanking]);
  const availCities = useMemo(() => Array.from(new Set(inheritedRanking.map((r) => r.city).filter((v) => v && v !== "-"))).sort(), [inheritedRanking]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return baseRanking.filter((r) => {
      if (q && !(`${r.name} ${r.city} ${r.state}`.toLowerCase().includes(q))) return false;
      if (minDiscount !== "" && r.overallDiscount < Number(minDiscount)) return false;
      if (maxDiscount !== "" && r.overallDiscount > Number(maxDiscount)) return false;
      if (minRevenue !== "" && r.totalRevenue < Number(minRevenue)) return false;
      if (maxRevenue !== "" && r.totalRevenue > Number(maxRevenue)) return false;
      return true;
    });
  }, [baseRanking, search, minDiscount, maxDiscount, minRevenue, maxRevenue]);

  const { sortedRows, sort, toggleSort } = useTableSort(rows, {
    defaultKey: "rank",
    defaultOrder: "asc",
    accessors: {
      rank: (r) => r.rank,
      name: (r) => r.name || "",
      city: (r) => r.city || "",
      state: (r) => r.state || "",
      totalRevenue: (r) => r.totalRevenue || 0,
      totalMrp: (r) => r.totalMrp || 0,
      overallDiscount: (r) => r.overallDiscount || 0,
      quotationCount: (r) => r.quotationCount || 0,
      totalUnits: (r) => r.totalUnits || 0,
    },
  });

  useEffect(() => { setPage(1); }, [search, pageStates, pageCities, pageBrands, minDiscount, maxDiscount, minRevenue, maxRevenue, dateFrom, dateTo, baseRanking]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = sortedRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const activeFilterChips = [];
  activeFilterChips.push(`Date: ${dateFrom || "…"} → ${dateTo || "…"}`);
  if (inherited.selectedBrands.length) activeFilterChips.push(`Brands: ${inherited.selectedBrands.length}`);
  if (inherited.selectedSkus.length) activeFilterChips.push(`SKUs: ${inherited.selectedSkus.length}`);
  if (inherited.selectedSellers.length) activeFilterChips.push(`Sellers: ${inherited.selectedSellers.length}`);
  if (inherited.selectedCities.length) activeFilterChips.push(`Cities: ${inherited.selectedCities.length}`);
  if (inherited.selectedStates.length) activeFilterChips.push(`States: ${inherited.selectedStates.length}`);
  if (!activeFilterChips.length) activeFilterChips.push("All time · no filters");

  const clearPageFilters = () => {
    setSearch(""); setPageStates([]); setPageCities([]); setPageBrands([]);
    setMinDiscount(""); setMaxDiscount(""); setMinRevenue(""); setMaxRevenue("");
  };

  // Export the currently filtered + sorted dataset (respects date range, search, brand/city/state, discount & revenue ranges).
  const handleExport = () => {
    const data = sortedRows.map((r) => ({
      "Rank": r.rank,
      "Seller Name": r.name || "",
      "Seller Code": sellerMap[r.id]?.seller_code || "",
      "City": r.city || "",
      "State": r.state || "",
      "Total Revenue": Math.round(r.totalRevenue || 0),
      "Total MRP": Math.round(r.totalMrp || 0),
      "Overall Discount %": Number((r.overallDiscount || 0).toFixed(2)),
      "Quotations": r.quotationCount || 0,
      "Units Sold": r.totalUnits || 0,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Seller Ranking");
    XLSX.writeFile(wb, `seller-revenue-ranking_${dateFrom || "all"}_to_${dateTo || "all"}.xlsx`);
  };

  return (
    <div className="space-y-4" data-testid="seller-revenue-ranking-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Button variant="ghost" size="sm" className="h-7 px-2 mb-1 -ml-2 text-muted-foreground" onClick={() => navigate("/seller-insights")} data-testid="srr-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Seller Insights
          </Button>
          <h1 className="text-2xl font-bold tracking-tight font-heading flex items-center gap-2">
            <Trophy className="h-6 w-6 text-amber-500" /> Seller Revenue Ranking
          </h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-sm text-muted-foreground" data-testid="srr-total-sellers">{sortedRows.length} seller{sortedRows.length === 1 ? "" : "s"}</span>
            {activeFilterChips.map((c, i) => (
              <span key={i} className="text-[11px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{c}</span>
            ))}
          </div>
        </div>
        <Button onClick={handleExport} disabled={loading || sortedRows.length === 0} className="bg-[#14b8a6] hover:bg-[#0f9e8f] text-white" data-testid="srr-export-btn">
          <Download className="h-4 w-4 mr-2" /> Export to Excel
        </Button>
      </div>

      {/* Filters + Search */}
      <Card className="border-[#E8E8E8]">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">From</span>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-[150px] text-xs" data-testid="srr-date-from" />
              <span className="text-xs text-muted-foreground">To</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-[150px] text-xs" data-testid="srr-date-to" />
            </div>
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search seller, city or state..." className="h-9 pl-8 text-xs" data-testid="srr-search" />
            </div>
            <MultiFilter label="State" open={stateOpen} setOpen={setStateOpen} options={availStates} selected={pageStates} setSelected={setPageStates} testId="srr-filter-state" />
            <MultiFilter label="City" open={cityOpen} setOpen={setCityOpen} options={availCities} selected={pageCities} setSelected={setPageCities} testId="srr-filter-city" />
            <MultiFilter label="Brand" open={brandOpen} setOpen={setBrandOpen} options={ALL_BRANDS} selected={pageBrands} setSelected={setPageBrands} testId="srr-filter-brand" />
          </div>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="text-muted-foreground">Discount %:</span>
            <Input type="number" value={minDiscount} onChange={(e) => setMinDiscount(e.target.value)} placeholder="Min" className="h-8 w-20 text-xs" data-testid="srr-min-discount" />
            <span className="text-muted-foreground">–</span>
            <Input type="number" value={maxDiscount} onChange={(e) => setMaxDiscount(e.target.value)} placeholder="Max" className="h-8 w-20 text-xs" data-testid="srr-max-discount" />
            <span className="text-muted-foreground ml-3">Revenue ₹:</span>
            <Input type="number" value={minRevenue} onChange={(e) => setMinRevenue(e.target.value)} placeholder="Min" className="h-8 w-28 text-xs" data-testid="srr-min-revenue" />
            <span className="text-muted-foreground">–</span>
            <Input type="number" value={maxRevenue} onChange={(e) => setMaxRevenue(e.target.value)} placeholder="Max" className="h-8 w-28 text-xs" data-testid="srr-max-revenue" />
            <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground" onClick={clearPageFilters} data-testid="srr-clear-filters"><X className="h-3 w-3 mr-1" />Clear</Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-[#E8E8E8]">
        <CardContent className="p-0">
          <div className="overflow-auto max-h-[60vh]">
            <Table>
              <TableHeader className="sticky top-0 z-20">
                <TableRow className="bg-slate-100 hover:bg-slate-100">
                  <SortableHeader sortKey="rank" sort={sort} onToggle={toggleSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Rank</SortableHeader>
                  <SortableHeader sortKey="name" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Seller Name</SortableHeader>
                  <SortableHeader sortKey="city" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">City</SortableHeader>
                  <SortableHeader sortKey="state" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">State</SortableHeader>
                  <SortableHeader sortKey="totalRevenue" sort={sort} onToggle={toggleSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Total Revenue</SortableHeader>
                  <SortableHeader sortKey="totalMrp" sort={sort} onToggle={toggleSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Total MRP</SortableHeader>
                  <SortableHeader sortKey="overallDiscount" sort={sort} onToggle={toggleSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Overall Disc %</SortableHeader>
                  <SortableHeader sortKey="quotationCount" sort={sort} onToggle={toggleSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Quotations</SortableHeader>
                  <SortableHeader sortKey="totalUnits" sort={sort} onToggle={toggleSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Units Sold</SortableHeader>
                  <TableCell className="bg-slate-100 border-b-2 border-slate-200 w-8"></TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground text-sm">Loading…</TableCell></TableRow>
                ) : pageRows.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground text-sm" data-testid="srr-empty">No sellers match the current filters.</TableCell></TableRow>
                ) : (
                  pageRows.map((r) => (
                    <TableRow
                      key={r.name}
                      className="cursor-pointer hover:bg-[#14b8a6]/5"
                      onClick={() => navigate(`/seller-drill-down/${encodeURIComponent(r.id || r.name)}`, { state: { sellerName: r.name } })}
                      data-testid={`srr-row-${r.rank}`}
                    >
                      <TableCell className="text-right font-mono text-xs">{r.rank}</TableCell>
                      <TableCell className="text-xs font-medium text-[#14b8a6]">{r.name}</TableCell>
                      <TableCell className="text-xs">{r.city || "—"}</TableCell>
                      <TableCell className="text-xs">{r.state || "—"}</TableCell>
                      <TableCell className="text-right text-xs font-mono">{fmtMoney(r.totalRevenue)}</TableCell>
                      <TableCell className="text-right text-xs font-mono">{fmtMoney(r.totalMrp)}</TableCell>
                      <TableCell className={`text-right text-xs font-mono ${r.overallDiscount > 62 ? "text-red-700 font-semibold" : ""}`}>{fmtPct(r.overallDiscount)}</TableCell>
                      <TableCell className="text-right text-xs font-mono">{fmtNum(r.quotationCount)}</TableCell>
                      <TableCell className="text-right text-xs font-mono">{fmtNum(r.totalUnits)}</TableCell>
                      <TableCell className="text-right"><Chevron className="h-4 w-4 text-muted-foreground" /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-xs text-muted-foreground">
              Showing {sortedRows.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, sortedRows.length)} of {sortedRows.length}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-8 px-2" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="srr-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-xs text-muted-foreground">Page {safePage} of {totalPages}</span>
              <Button variant="outline" size="sm" className="h-8 px-2" disabled={safePage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="srr-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
